import { ChangeEvent, memo, MouseEvent, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import IconEraser from '@picsart/icons/IconEraser';
import Checkbox from '@picsart/checkbox/Checkbox';
import View from '@picsart/view/View';
import Grid from '@picsart/grid/Grid';
import { Spacings } from '@picsart/cascade';
import { ControlSizesSM, InlineButton } from '@picsart/inlineButton';
import IconDelete from '@picsart/icons/IconDelete';

import { IBatchData } from 'types';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import { BatchDataStatus, TRIGGER_PREVIEW_TARGET } from 'configs/constants';
import GridItemLoader from 'pages/Batch/components/GridItemLoading';
import { BATCH_TOOLS, batchToolsList } from 'pages/Batch/configs';
import useMemoSelector from 'hooks/useMemoSelector';
import batchSlice from 'store/batch';
import { getUserData } from 'store/selectors/general';
import analytics from 'pages/Batch/utils/analytics';
import uiSlice from 'store/ui';
import watermarkGenerator from 'pages/Batch/utils/watermark/watermarkGenerator';
import loadingImage from 'utils/images/loadingImage';
import { resizeImage } from 'pages/Batch/utils/process/resizeProcess';
import { getBatchActiveColor, getBatchActiveTexture } from 'store/batch/selectors';

import GridItemBox from '../GridItemBox';
import useStyles from '../styles';

import testIds from './testIds';

interface IGridContent {
  onSelectImages: (key: string) => void;
  onDeleteImage: (key: string) => void;
  data: IBatchData[];
}

const GridContent = ({
  onSelectImages,
  onDeleteImage,
  data,
}: IGridContent) => {
  const dispatch = useDispatch();

  const {
    selectedKeys,
    uploadedData,
    activeTool,
    resizeParams,
    isWeb,
  } = useBatchData();

  const classes = useStyles({ isWeb });
  const { isSubscribed } = useMemoSelector(getUserData);
  const { activeColor, activeTexture } = useMemoSelector(state => ({
    activeColor: getBatchActiveColor(state),
    activeTexture: getBatchActiveTexture(state),
  }));

  const reversedData = useMemo(() => [...data].reverse(), [data]);
  const isUploadDone = useMemo(() => uploadedData.status === BatchDataStatus.DONE, [uploadedData]);

  const isSelected = useCallback((key: string) => !activeTool && selectedKeys.includes(key), [activeTool, selectedKeys]);

  const isUploaderLoading = useCallback((itemIndex: number) => !!uploadedData.count && uploadedData.count > itemIndex, [uploadedData]);

  const openEraser = useCallback((item: IBatchData) => {
    dispatch(batchSlice.actions.setEraserItem({
      ...item,
      result: { src: item.result.src },
      source: { src: item.source.src },
    }));
    analytics.clickEvent('tool_cutout');
    analytics.openTool('tool_cutout', false, [item.key]);
  }, [dispatch]);

  const handleSelectItem = (ev: ChangeEvent, item: IBatchData) => {
    onSelectImages(item.key);
  };

  const handleGridItemClick = useCallback(async (ev: MouseEvent, idx: number, item: IBatchData) => {
    const isAIProcessing = activeTool && [BATCH_TOOLS.removeBG, BATCH_TOOLS.enhance].includes(activeTool);
    let { src } = (item.pendingResult || item.result);
    let secondSrc = null;

    if (item.status !== BatchDataStatus.DONE && isAIProcessing) return;
    if (activeTool === BATCH_TOOLS.enhance) {
      src = item.pendingResult?.src || '';
      secondSrc = item.result.src;
    }

    // TODO: click event is slow, refactoring is required
    // Watermark Canvas element preview image case
    if (activeTool === BATCH_TOOLS.watermark && typeof src !== 'string') {
      const image = await loadingImage(item.result.src);
      const watermarkGeneratorFunc = watermarkGenerator.generate();
      const processedData = await watermarkGeneratorFunc(image) as { result: string; blob: Blob }; // TODO centralize types/interfaces
      src = processedData.result;
    } else if (activeTool && [BATCH_TOOLS.resize, BATCH_TOOLS.removeBG_resize].includes(activeTool) && resizeParams) {
      const image = await loadingImage(activeTool === BATCH_TOOLS.removeBG_resize ? item.pendingResult?.src || '' : item.result.src);
      let textureImage;
      if (activeTexture?.url) {
        textureImage = await loadingImage(activeTexture.url);
      }

      const processedData = await resizeImage(
        image,
        resizeParams.width || image.width,
        resizeParams.height || image.height,
        resizeParams.usePadding,
        activeColor,
        textureImage,
      ) as { result: string; blob: Blob };

      src = processedData.result;
      secondSrc = activeTool === BATCH_TOOLS.removeBG_resize ? item.pendingResult?.src || '' : item.result.src;
    }

    const { ariaLabel } = (ev.target as HTMLElement);
    if (ariaLabel === TRIGGER_PREVIEW_TARGET) {
      dispatch(uiSlice.actions.setPreviewImage({ src, itemKey: item.key, secondSrc }));
    }
  }, [activeTool, dispatch, resizeParams, activeColor, activeTexture]);

  // Should be removed after having a solution from the DS (editor Layout) side
  const contentPaddingBottom = useMemo(() => !isWeb && (batchToolsList.find(tool => tool.type === activeTool) as any)?.bottomSheetHeight, [isWeb, activeTool]);

  return (
    <div className={classes.mainContentGridWrap}>
      <div className={classes.mainContentGrid} style={{ paddingBottom: contentPaddingBottom }}>
        {reversedData.map((item, idx) => {
          const itemSrc = (item.pendingResult || item.result).src;
          const isSelectedItem = isSelected(item.key);
          const isUploader = isUploaderLoading(idx);
          return (
            <GridItemBox
              isSelectedItem={isSelectedItem}
              isUploadDone={isUploadDone}
              itemKey={item.key}
              imgSrc={itemSrc}
              key={item.key}
              onClick={(ev: MouseEvent<HTMLElement>) => handleGridItemClick(ev, idx, item)}
            >
              {!item.hideStatusIndicator && (activeTool || isUploader) && (
                <GridItemLoader status={isUploader && !activeTool ? uploadedData.status : item.status} />
              )}
              {/* WILL BE USED */}
              {/* {!waitingUserSubscription && item.isProcessed && !isSubscribed && <img src={wmImage?.src} alt="vmImage" className={classes.watermark} />} */}
              <View
                dataTestId={testIds.item}
                aria-label={TRIGGER_PREVIEW_TARGET}
                width="100%"
                height="100%"
                position="absolute"
                insetInlineStart={Spacings.s0}
                insetBlockStart={Spacings.s0}
                className={classes.gridOverlay}
              >
                {(!activeTool && isWeb) && (
                  <View margin={Spacings.s8}>
                    <Checkbox className={classes.selectCheckbox} data-testid={testIds.itemSelect} isChecked={isSelectedItem} onChange={(ev: ChangeEvent) => handleSelectItem(ev, item)} />
                  </View>
                )}
                <View
                  position="absolute"
                  insetInlineEnd={Spacings.s12}
                  insetBlockEnd={Spacings.s12}
                  className={classes.gridBtnsContainer}
                >
                  <Grid
                    gap={Spacings.s8}
                    columns={`repeat(var(${!isSubscribed ? 1 : 2}), auto)`}
                  >
                    {!activeTool && (
                      <InlineButton
                        onClick={() => onDeleteImage(item.key)}
                        data-testid={testIds.itemDelete}
                        size={ControlSizesSM}
                        centerIcon={IconDelete}
                        isWithoutBorder
                      />
                    )}
                    {activeTool === BATCH_TOOLS.removeBG && isWeb && (
                      <InlineButton
                        onClick={() => openEraser(item)}
                        data-testid={testIds.itemErase}
                        size={ControlSizesSM}
                        centerIcon={IconEraser}
                        isWithoutBorder
                      />
                    )}
                  </Grid>
                </View>
              </View>
            </GridItemBox>
          );
        })}
      </div>
    </div>
  );
};

export default memo(GridContent);
